import { VOInstrument, VOMelody } from "../model/DataStructure.js";
import { MultiPlayer } from "./MultiPlayer.js";
import { Player, PlayerState } from "./Player.js";

export interface SequenceOptions<Item extends SequenceItem = SequenceItem> {
    instruments: VOInstrument[];
    audioContext: AudioContext;

    onCompleteMelody?: (s: Sequence<Item>) => void;
    onComplete?: (s: Sequence<Item>) => void;
    onTime?: (s: Sequence<Item>) => void;
    onChangeItems?: (s: Sequence<Item>) => void;
    onChangeItem?: (s: Sequence<Item>) => void;
    onChangeState?: (s: Sequence<Item>) => void;
}

export class SequenceItem {
    melody: VOMelody;
    uid: number;
    pitch: number;
    speed: number;
    playing: boolean;
}

export class Sequence<Item extends SequenceItem = SequenceItem>{

    public onCompleteMelody = e5.Signal.create<[Sequence<Item>]>();
    public onComplete = e5.Signal.create<[Sequence<Item>]>();
    public onTime = e5.Signal.create<[Sequence<Item>]>();
    public onChangeItems = e5.Signal.create<[Sequence<Item>]>();
    public onChangeItem = e5.Signal.create<[Sequence<Item>]>();
    public onChangeState = e5.Signal.create<[Sequence<Item>]>();

    private _player: Player;
    private _items: Item[] = [];
    private _duration: number = 0;
    private _current: Item = null;

    constructor(options: SequenceOptions<Item>) {

        const player = new MultiPlayer({
            instruments: options.instruments,
            stopInstruments: false,
            audioContext: options.audioContext
        });
        player.onComplete.add(() => {
            let next = this.getNext();
            this.onCompleteMelody.dispatch(this);
            if (next) {
                this.next();
                this.play();
            } else {
                this.loadItem(null);
                this.onComplete.dispatch(this);
                this.onTime.dispatch(this);
            }
        });
        player.onTime.add(() => {
            this.onTime.dispatch(this);
        });
        player.onStateChange.add(() => {
            this.onChangeState.dispatch(this);
        });
        this._player = player;

        if (typeof options.onCompleteMelody === "function") {
            this.onCompleteMelody.add(options.onCompleteMelody);
        }

        if (typeof options.onComplete === "function") {
            this.onComplete.add(options.onComplete);
        }

        if (typeof options.onTime === "function") {
            this.onTime.add(options.onTime);
        }

        if (typeof options.onChangeItems === "function") {
            this.onChangeItems.add(options.onChangeItems);
        }

        if (typeof options.onChangeItem === "function") {
            this.onChangeItem.add(options.onChangeItem);
        }

        if (typeof options.onChangeState === "function") {
            this.onChangeState.add(options.onChangeState);
        }
    }

    public get noteCount(): number {   
        return this._items.reduce((sum, item) => sum + item.melody.noteCount, 0);
    }

    public get items(): Item[] {
        return this._items;
    }

    private getCurrentIndex(): number {
        if (!this._current) {
            return -1;
        }
        return this._items.indexOf(this._current);
    }

    public get current(): Item {
        return this._current;
    }

    public getPrev(around: boolean = false): Item {
        const idx = this.getCurrentIndex();
        if (idx <= 0) {
            if (around && this._items.length > 0) {
                return this._items[this._items.length - 1];
            }
            return null;
        }
        return this._items[idx - 1];
    }

    public getNext(around: boolean = false): Item {
        const idx = this.getCurrentIndex();
        if (idx < 0
            || idx >= this._items.length - 1) {
            if (around && this._items.length > 0) {
                return this._items[0];
            }
            return null;
        }
        return this._items[idx + 1];
    }

    public prev(around: boolean = false): void {
        let prev = this.getPrev(around);
        if (prev) {
            this.loadItem(prev);
        }
    }

    public next(around: boolean = false): void {
        let next = this.getNext(around);
        if (next) {
            this.loadItem(next);
        }
    }

    private loadItem(item: Item): void {
        if (this._current === item) {
            return;
        }
        this._current = item;

        if (this._current) {
            this._player.load(
                this._current.melody,
                {
                    pitch: item.pitch,
                    speed: item.speed
                }
            );
        } else {
            this._player.load(null);
        }

        this.onChangeItem.dispatch(this);
        this.onTime.dispatch(this);
    }

    public get state(): PlayerState {
        return this._player.state;
    }

    public updateItem(item: Item): void {

        if (this._items.length === 0
            || this._items.indexOf(item) === -1) {
            //TODO: show error???
            return;
        }

        if (item === this._current) {
            this._player.speed = item.speed;
            this._player.pitch = item.pitch;
        }

        this.onChangeItems.dispatch(this);
    }

    public load(items: Item[]): void {
        if (this._current
            && items.indexOf(this._current) === -1) {
            this._items = items.slice();
            this.loadItem(null);
        } else {
            this._items = items.slice();
        }

        if (this._items.length === 0) {
            this._player.stop();
        }

        this.onChangeItems.dispatch(this);
    }

    public seekToItem(item: Item) {
        let time = this.getStartTimeOfItem(item);
        if (time != -1) {
            this.time = time;
        }
    }

    private getStartTimeOfItem(item: Item): number {
        if (this._items.indexOf(item) === -1) {
            return -1;
        }
        let d = 0;
        for (let it of this._items) {
            if (it === item) {
                return d;
            }
            d += it.melody.duration / it.speed;
        }

        return -1;
    }

    public toggle() {
        if (this.state === "playing") {
            this.pause();
        } else {
            this.play();
        }
    }

    public stop() {
        this._player.stop();
        this.time = 0;
        this.loadItem(null);
    }

    public play() {
        if (this._items.length === 0) {
            return;
        }
        if (!this._current) {
            this.loadItem(this._items[0]);
        }
        this._player.play();
    }

    public pause() {
        if (this._items.length === 0) {
            return;
        }
        this._player.pause();
    }

    public get duration(): number {
        this._duration = 0;
        for (let item of this._items) {
            this._duration += item.melody.duration / item.speed;
        }
        return this._duration;
    }

    public get time(): number {
        if (!this._current) {
            // console.log("MMMMM");
            return 0;
        }

        let d = 0;
        for (let item of this._items) {
            if (item === this._current) {
                d += this._player.time;
                return d;
            }
            d += item.melody.duration / item.speed;
        }

        // console.log("WAHHH", d);
        return d;
    }

    public set time(value: number) {
        value = e5.Calc.clamp(value, 0, this.duration);

        let isPlaying = this.state === "playing";

        let d = 0;
        for (let i = 0; i < this._items.length; ++i) {
            let item = this._items[i];
            let dur = item.melody.duration / item.speed;
            if (value >= d && value < (d + dur)
            || i === this._items.length - 1) {
                let timeIn = value - d;
                // console.log("SEEK", item.melody.name, timeIn, item === this._current);
                if (item === this._current) {
                    this._player.time = timeIn;
                    if (isPlaying) {
                        this._player.play();
                    }
                } else {
                    this.loadItem(item);
                    this._player.time = timeIn;
                    if (isPlaying) {
                        this._player.play();
                    }
                }
                this.onTime.dispatch(this);
                return;
            }
            d += dur;
        }

        // console.log("AHHHHH", value, this.duration);
    }

    public get timeCurrent(): number {
        if (!this._current) {
            return 0;
        }
        return this._player.time;
    }

    public get ratioCurrent(): number {
        if (!this._current) {
            return 0;
        }
        return e5.Calc.clamp(this._player.time / this._player.duration, 0, 1);
    }
}